import { useEffect, useRef, useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import isEqual from 'lodash.isequal';
import { INTERNATIONAL_SUPPORT } from '@rsos/capstone/src/constants/capabilities';
import { useZoomAndLocationContext } from '@rsos/components/capstone/common/Map/ZoomAndLocationContext';
import { ALERTS, EMERGENCY_CALLS } from '@rsos/constants/incidentTypes';
import { LOCATION, SECONDARY_LOCATION } from '@rsos/constants/locationTypes';
import { PHASE } from '@rsos/constants/locationTypes';
import { useFlag } from '@rsos/flags';
import { DEFAULT_ZOOM_LEVEL } from '@rsos/maps/constants';
import { ESRI } from '@rsos/maps/constants/mapProviders';
import { MARKER_TYPE_GRAPHIC } from '@rsos/maps/constants/markerTypes';
import { createCircle } from '@rsos/maps/interface/circles';
import { createMarker } from '@rsos/maps/interface/markers';
import {
  createPolyline,
  getPolylineOption,
} from '@rsos/maps/interface/polylines';
import {
  FOREGROUND,
  BACKGROUND,
} from '@rsos/maps/interface/polylines/constants';
import { createGraphicsLayer } from '@rsos/maps/providers/esri/graphics/layers';
import {
  useCallQueueFilters,
  callQueueSelector,
} from '@rsos/reduxDataStore/src/features/selectors/callQueueSelector';
import { workstationPositionSelector } from '@rsos/reduxDataStore/src/features/selectors/workstationSelectors';
import checkAniAliAccessSelector from '@rsos/utils/checkAniAliAccessSelector';
import checkCallStatusAccessSelector from '@rsos/utils/checkCallStatusAccessSelector';
import {
  getMostRecentLoc,
  getMostRecentLocations,
  getMostRecentLocationWithLocation,
} from '@rsos/utils/getMostRecentLoc';
import isValidCoordinates from '@rsos/utils/isValidCoordinates';
import { panMapTo } from '@rsos/utils/panMapToCoordinate';
import useIsIRP from '@rsos/utils/useIsIRP';
import usePrevious from '@rsos/utils/usePrevious';
import {
  CALL_MARKERS_POLYLINE_LAYER,
  CALL_MARKER_CIRCLES_LAYER,
  CALL_MARKERS_LAYER,
} from '../../../../../constants/esriMapLayerIds';
import { shouldMarkerDim } from '../../../../Map/utils/callFiltering';
import { isSourceAniAli } from '../../../../MostRecentLocationCard/helpers';
import PopupPortal from '../../PopupPortal';
import '../OverridePOPUP.css';
import CallPopup from './CallPopup';
import {
  getCallMarkerIcon,
  getCallMarkerHoverIcon,
  getCallMarkerSize,
  getCallCircleColor,
} from './callMarkerIcons';
import { isIgnoreType } from './ignoreUtils';

const baseEsriCircle = {
  type: 'simple-fill',
  style: 'solid',
  color: getCallCircleColor(),
  outline: { width: 2, color: getCallCircleColor() },
};

// TODO: the icons need to be redone on the asset side. the sizes don't scale with esri maps. they are converted to PNGs and are blurry.
// TODO: helper function for icon sizes

/**
 * ESRI MAPS
 * Define Esri maps specific things outside of the React lifecycle
 */
const portalMount = document.createElement('div');
let prevPopupData = null;

export const CallMarkers = ({
  activeQueueTab,
  hoveredCall,
  map,
  onMarkerAdd,
  onMarkerClick,
  onMarkerHover,
  onRemoveID,
  removedID,
  hoveredFrom,
  isMapIconDisabled,
}) => {
  const { userPannedAway } = useZoomAndLocationContext();
  const isSoupMapImprovementsOn = useFlag('team-soup-map-improvements');
  const [callMarkers, setCallMarkers] = useState([]);
  const [polylineLayer, setPolylineLayer] = useState(null);
  const [callMarkersLayer, setCallMarkersLayer] = useState(null);
  const [callMarkerCircles, setCallMarkerCircles] = useState([]);
  const [callMarkerCirclesLayer, setCallMarkerCirclesLayer] = useState(null);
  const [callMarkersLayerClickHandler, setCallMarkersLayerClickHandler] =
    useState(null);
  const [markerHoveredData, setMarkerHoveredData] = useState({
    callerID: null,
    isSecondaryLocation: false,
  });
  const [popupData, setPopupData] = useState(null);
  const { queryURI } = useIsIRP();

  const callMarkersLayerHoverHandlerRef = useRef();

  const hasAniAliAccess = useSelector(state =>
    checkAniAliAccessSelector({
      psaps: state.psaps,
      sinatra: state.sinatra,
    }),
  );

  const hasCallStatusAccess =
    useSelector(state =>
      checkCallStatusAccessSelector({
        psaps: state.psaps,
      }),
    ) && hasAniAliAccess;

  const isInternational = useSelector(
    state =>
      !!(
        state.psaps.currentPSAP.activeCapabilities &&
        state.psaps.currentPSAP.activeCapabilities[INTERNATIONAL_SUPPORT]
      ),
  );

  // Call taker mode filtering for Esri maps
  const { callFilters, hasActiveCallFilters } = useCallQueueFilters();
  const workStation = useSelector(workstationPositionSelector);

  const callQueue = useSelector(state =>
    callQueueSelector(state.emergencyCalls.callQueue, hasAniAliAccess),
  );
  const selectedCallerID = useSelector(
    state => state.emergencyCalls.selectedCallerID,
  );
  const selectedCall = useMemo(
    () => callQueue[selectedCallerID],
    [callQueue, selectedCallerID],
  );
  const previousCallQueue = usePrevious(callQueue);

  const numberWithNewLocation = useSelector(
    state => state.emergencyCalls.numberWithNewLocation,
  );

  const selectedAlertID = useSelector(state => state.incidents.selectedAlertID);
  const hasW3W = useSelector(state => state.what3words.searched?.coordinates);

  const hasLocation = getMostRecentLoc(callQueue[selectedCallerID]);
  const isValidCoords = isValidCoordinates(hasLocation);

  const isAllView = useMemo(
    () =>
      (!selectedAlertID && (!selectedCallerID || !isValidCoords)) ||
      isIgnoreType(selectedCall),
    [selectedAlertID, selectedCallerID, isValidCoords, selectedCall],
  );

  const prevIsAllView = usePrevious(isAllView);

  const selectedOrHoveredCallerID =
    callQueue?.[selectedCallerID || hoveredCall];

  const mostRecentCallStatus =
    (hasCallStatusAccess &&
      selectedOrHoveredCallerID?.callStatuses?.[
        selectedOrHoveredCallerID?.callStatuses?.length - 1
      ]) ||
    null;

  const clearAll = async () => {
    await new Promise(resolve => {
      clearAllMarkers();
      clearAllCircles();
      clearAllPolylines();
      resolve();
    });
  };

  async function clearAllMarkers() {
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI:
          if (callMarkersLayer) {
            callMarkersLayer.removeAll();
          }
          if (callMarkers.length) {
            setCallMarkers([]);
          }
          break;
        default:
          return null;
      }
      resolve();
    });
  }
  async function clearAllPolylines() {
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI:
          if (polylineLayer) {
            polylineLayer.removeAll();
          }
          break;
        default:
          return null;
      }
      resolve();
    });
  }

  async function clearAllCircles() {
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI:
          if (callMarkerCirclesLayer) {
            callMarkerCirclesLayer.removeAll();
          }
          if (callMarkerCircles.length) {
            setCallMarkerCircles([]);
          }
          break;
        default:
          return null;
      }
      resolve();
    });
  }

  const getIncomingMarkerType = callerID => {
    if (!callerID) return;
    const currCalls = callQueue[callerID]?.core;
    let incomingMarkerType = LOCATION;

    if (previousCallQueue && previousCallQueue[callerID]) {
      const prevCalls = previousCallQueue[callerID]?.core;
      incomingMarkerType =
        prevCalls.secondaryLocations?.length !==
        currCalls.secondaryLocations?.length
          ? SECONDARY_LOCATION
          : LOCATION;
    } else {
      incomingMarkerType = currCalls?.secondaryLocations?.length
        ? SECONDARY_LOCATION
        : LOCATION;
    }
    return incomingMarkerType;
  };

  //draws polyline between 2 markers
  const drawLine = async locations => {
    await new Promise(resolve => {
      const locA = locations[0];
      const locB = locations[1];
      const latlngA = {
        lat: parseFloat(locA.latitude),
        lng: parseFloat(locA.longitude),
      };
      const latlngB = {
        lat: parseFloat(locB.latitude),
        lng: parseFloat(locB.longitude),
      };
      const polyline = {
        type: 'polyline',
        paths: [
          [latlngA.lng, latlngA.lat],
          [latlngB.lng, latlngB.lat],
        ],
      };

      const foreground = getPolylineOption(FOREGROUND);
      const background = getPolylineOption(BACKGROUND);

      createPolyline({
        mapObject: map,
        options: {
          geometry: polyline,
          symbol: background,
          layer: polylineLayer,
        },
      }).then(resolvedPolyline => {
        polylineLayer.graphics.add(resolvedPolyline.instance);
      });
      createPolyline({
        mapObject: map,
        options: {
          geometry: polyline,
          symbol: foreground,
          layer: polylineLayer,
        },
      }).then(resolvedPolyline => {
        polylineLayer.graphics.add(resolvedPolyline.instance);
      });

      resolve();
    });
  };

  // Draw all markers for all calls view or detailed view
  const drawMarkers = async isUserPannedAway => {
    await new Promise(resolve => {
      switch (map.mapProvider) {
        case ESRI: {
          if (isAllView) {
            if (Object.keys(callQueue).length) {
              const mostRecentCallsLocations = Object.keys(callQueue)
                .map(call => {
                  const { mostRecentLocation, mostRecentSecondaryLocation } =
                    getMostRecentLocations(callQueue[call]);
                  return mostRecentLocation || mostRecentSecondaryLocation;
                })
                .filter(call => {
                  return call?.longitude && call?.latitude;
                });

              // Assume the GraphicsLayer is already created
              const markers = mostRecentCallsLocations.map(location => {
                const callerID = location.caller_id;
                const latlng = {
                  lat: parseFloat(location.latitude),
                  lng: parseFloat(location.longitude),
                };
                const isAniAli = isSourceAniAli(location);
                const isSecondary =
                  isAniAli &&
                  location?.class_of_service_category?.startsWith(PHASE);
                // Determine if marker should be dimmed based on call taker mode filters
                const shouldDim = shouldMarkerDim(callerID, {
                  activeQueueTab,
                  hasActiveCallFilters,
                  callQueue,
                  callFilters,
                  workStation,
                });
                const isOpaque = !shouldDim;
                const markerIcon = getCallMarkerIcon({
                  call: callQueue[callerID],
                  isAllView,
                  isMostRecent: true,
                  isSecondary,
                  hasCallStatusAccess,
                  isOpaque,
                });
                const markerSize = getCallMarkerSize(
                  isAllView,
                  false,
                  callQueue[callerID],
                );

                return createMarker({
                  mapObject: map,
                  options: {
                    point: latlng,
                    icon: markerIcon,
                    iconSize: markerSize,
                    custom: {
                      callerID,
                      secondary: isSecondary,
                    },
                    layer: callMarkersLayer,
                  },
                  markerType: MARKER_TYPE_GRAPHIC,
                }).then(resolvedMarker => {
                  callMarkersLayer.add(resolvedMarker.instance);
                  return resolvedMarker;
                });
              });
              Promise.all(markers).then(resolvedMarkers => {
                setCallMarkers(resolvedMarkers);
              });
              setCallMarkerCircles([]);
            }
          } else if (selectedCallerID && callQueue[selectedCallerID]) {
            let callLocations = callQueue[selectedCallerID].core?.locations;
            const callLocationsLength = callLocations?.length;
            const mostRecentCallIdx = callLocationsLength - 1;
            const { mostRecentLocation, mostRecentSecondaryLocation } =
              getMostRecentLocations(callQueue[selectedCallerID]);

            let markers = [];
            let circles = [];

            if (mostRecentSecondaryLocation) {
              const callerID = mostRecentSecondaryLocation.caller_id;
              const latlng = {
                lat: parseFloat(mostRecentSecondaryLocation.latitude),
                lng: parseFloat(mostRecentSecondaryLocation.longitude),
              };
              const markerSize = getCallMarkerSize(
                isAllView,
                true,
                callQueue[callerID],
              );
              // Determine if marker should be dimmed based on call taker mode filters
              const shouldDim = shouldMarkerDim(callerID, {
                activeQueueTab,
                hasActiveCallFilters,
                callQueue,
                callFilters,
                workStation,
              });
              const isOpaque = !shouldDim;
              const markerIcon = getCallMarkerIcon({
                call: callQueue[callerID],
                isAllView,
                isMostRecent: true,
                isSecondary: true,
                hasCallStatusAccess,
                isOpaque,
              });

              const marker = createMarker({
                mapObject: map,
                options: {
                  point: latlng,
                  icon: markerIcon,
                  iconSize: markerSize,
                  custom: {
                    callerID,
                    secondary: true,
                  },
                  layer: callMarkersLayer,
                },
                markerType: MARKER_TYPE_GRAPHIC,
              }).then(resolvedMarker => {
                callMarkersLayer.add(resolvedMarker.instance);
                return resolvedMarker;
              });
              // Append marker to front of array for secondary locations
              markers = [marker, ...markers];

              // Transparent circle until requirements change
              const circleColorTransparent = getCallCircleColor(true);
              const circleOptions = {
                center: [latlng.lng, latlng.lat],
                radius:
                  location.uncertainty_radius < 0
                    ? 0
                    : parseFloat(location.uncertainty_radius),
              };
              const circle = createCircle({
                mapObject: map,
                circleOptions: {
                  ...circleOptions,
                  custom: {
                    callerID,
                    secondary: true,
                  },
                  layer: callMarkerCirclesLayer,
                },
                graphicOptions: {
                  symbol: {
                    ...baseEsriCircle,
                    color: circleColorTransparent,
                    outline: {
                      ...baseEsriCircle.outline,
                      color: circleColorTransparent,
                    },
                  },
                  visible: true,
                },
              }).then(resolvedMarkerCircle => {
                callMarkerCirclesLayer.add(resolvedMarkerCircle.instance);
                return resolvedMarkerCircle;
              });
              // Append circle to front of array for secondary locations
              circles = [circle, ...circles];
            }
            if (callLocationsLength) {
              const primaryCircles = callLocations.map((location, idx) => {
                const callerID = location.caller_id;
                const latlng = {
                  lat: parseFloat(location.latitude),
                  lng: parseFloat(location.longitude),
                };
                const circleOptions = {
                  center: [latlng.lng, latlng.lat],
                  radius:
                    location.uncertainty_radius < 0
                      ? 0
                      : parseFloat(location.uncertainty_radius),
                };
                const circleColor = getCallCircleColor(idx < mostRecentCallIdx);

                return createCircle({
                  mapObject: map,
                  circleOptions: {
                    ...circleOptions,
                    custom: {
                      callerID,
                      secondary: false,
                    },
                    layer: callMarkerCirclesLayer,
                  },
                  graphicOptions: {
                    symbol: {
                      ...baseEsriCircle,
                      color: circleColor,
                      outline: {
                        ...baseEsriCircle.outline,
                        color: circleColor,
                      },
                    },
                    visible: true, // hide circle on load
                  },
                }).then(resolvedMarkerCircle => {
                  callMarkerCirclesLayer.add(resolvedMarkerCircle.instance);
                  return resolvedMarkerCircle;
                });
              });

              const primaryMarkers = callLocations.map((location, idx) => {
                const callerID = location.caller_id;
                const latlng = {
                  lat: parseFloat(location.latitude),
                  lng: parseFloat(location.longitude),
                };
                // Determine if marker should be dimmed based on call taker mode filters
                const shouldDim = shouldMarkerDim(callerID, {
                  activeQueueTab,
                  hasActiveCallFilters,
                  callQueue,
                  callFilters,
                  workStation,
                });
                const isOpaque = !shouldDim;
                const markerIcon = getCallMarkerIcon({
                  call: callQueue[callerID],
                  isAllView,
                  isMostRecent: idx === mostRecentCallIdx,
                  isSecondary: false,
                  hasCallStatusAccess,
                  isOpaque,
                });

                const markerSize = getCallMarkerSize(
                  isAllView,
                  idx === mostRecentCallIdx,
                  callQueue[callerID],
                );

                return createMarker({
                  mapObject: map,
                  options: {
                    point: latlng,
                    icon: markerIcon,
                    iconSize: markerSize,
                    custom: {
                      callerID,
                      secondary: false,
                    },
                    layer: callMarkersLayer,
                  },
                  markerType: MARKER_TYPE_GRAPHIC,
                }).then(resolvedMarker => {
                  callMarkersLayer.add(resolvedMarker.instance);
                  return resolvedMarker;
                });
              });
              markers = [...markers, ...primaryMarkers];
              circles = [...circles, ...primaryCircles];
            }

            Promise.all(markers).then(resolvedMarkers => {
              setCallMarkers(resolvedMarkers);
            });
            Promise.all(circles).then(resolvedMarkerCircles => {
              setCallMarkerCircles(resolvedMarkerCircles);
            });

            if (callLocations?.length && mostRecentSecondaryLocation) {
              drawLine([mostRecentLocation, mostRecentSecondaryLocation]);
            }

            const latlng = {
              lat: parseFloat(
                mostRecentLocation?.latitude ||
                  mostRecentSecondaryLocation?.latitude,
              ),
              lng: parseFloat(
                mostRecentLocation?.longitude ||
                  mostRecentSecondaryLocation?.longitude,
              ),
            };

            const currentZoomLevel = map.mapInstance.view.zoom;
            const zoom =
              prevIsAllView || currentZoomLevel === -1
                ? DEFAULT_ZOOM_LEVEL
                : currentZoomLevel;

            if (
              !isIgnoreType(mostRecentLocation) &&
              latlng?.lng &&
              latlng?.lat &&
              !isSoupMapImprovementsOn
            ) {
              panMapTo({
                mapProvider: ESRI,
                mapInstance: map.mapInstance,
                latitude: latlng.lat,
                longitude: latlng.lng,
                zoom,
              });
            } else if (
              !isIgnoreType(mostRecentLocation) &&
              isSoupMapImprovementsOn
            ) {
              // Will now pan to the most recent location whether
              // its secondary or primary.  The old code always pans to the
              // most recent primary location.
              const mostRecentLocationWithLocation =
                getMostRecentLocationWithLocation(callQueue[selectedCallerID]);
              if (!isUserPannedAway) {
                panMapTo({
                  mapProvider: ESRI,
                  mapInstance: map.mapInstance,
                  latitude: mostRecentLocationWithLocation.latitude,
                  longitude: mostRecentLocationWithLocation.longitude,
                  zoom:
                    map.mapInstance.view.zoom < DEFAULT_ZOOM_LEVEL
                      ? DEFAULT_ZOOM_LEVEL
                      : null,
                });
              }
            }
          }
          /**
           * Handling race condition for iRP users
           * where `numberWithNewLocation` is updated after an automatic query for location
           * that results in a duplicate marker mismatch between the all view and detailed view
           */

          onMarkerAdd();
          break;
        }
        default:
          return null;
      }
      resolve();
    });
  };

  const handleMarkerClick = () => {
    switch (map.mapProvider) {
      case ESRI:
        if (isAllView) {
          if (callMarkersLayer?.graphics?.items.length) {
            const clickHandler = map.mapInstance.view.on('click', evt => {
              const opts = {
                include: callMarkersLayer,
              };
              map.mapInstance.view.hitTest(evt, opts).then(response => {
                if (response?.results.length) {
                  const clickedMarker = callMarkers.find(marker => {
                    // assume no overlapping markers
                    return (
                      marker?.instance?.uid ===
                        response?.results[0]?.graphic?.uid && !isMapIconDisabled
                    );
                  });
                  if (clickedMarker) {
                    map.mapInstance.view.zoom = 13;
                    map.mapInstance.view.center = [
                      clickedMarker.instance.point.x,
                      clickedMarker.instance.point.y,
                    ];
                    onMarkerClick(clickedMarker.custom.callerID, isAllView);
                  }
                }
              });
            });
            setCallMarkersLayerClickHandler(clickHandler);
          }
        }
        break;
      default:
        return null;
    }
  };

  /**
   * @function handleMarkerHover
   * handleMarkerCircle's logic has been port into handleMarkerHover
   * markers and circle's length should be the same.
   * if there is secondary loc it will be in index [0]
   * only need to track if marker's UID === response's UID
   * then use the idx to change circle's color
   * markers : [{secondaryLoc,idx:0},{idx:1},{idx:2},{idx:3}]
   * circles : [{secondaryLoc,idx:0},{idx:1},{idx:2},{idx:3}]
   */
  const handleMarkerHover = () => {
    switch (map.mapProvider) {
      case ESRI:
        if (isMapIconDisabled) return;

        if (callMarkersLayer?.graphics?.items.length) {
          const hoverHandler = map.mapInstance.view.on('pointer-move', evt => {
            const opts = {
              include: callMarkersLayer,
            };
            if (isAllView) {
              map.mapInstance.view.hitTest(evt, opts).then(response => {
                if (response?.results.length) {
                  callMarkers.find(marker => {
                    if (
                      marker?.instance?.uid ===
                      response?.results[0]?.graphic?.uid
                    ) {
                      // Marker will change based on the hoveredCall hook
                      setMarkerHoveredData({
                        callerID: marker.custom.callerID,
                        isSecondaryLocation: marker.custom.secondary,
                      });
                    }
                  });
                } else {
                  setMarkerHoveredData({
                    callerID: null,
                    isSecondaryLocation: false,
                  });
                }
              });
            } else {
              map.mapInstance.view.hitTest(evt, opts).then(response => {
                const mostRecentMarkerIdx = callMarkers.length - 1;
                if (response?.results.length) {
                  callMarkers.forEach((marker, idx) => {
                    const call = callQueue[marker.custom.callerID];
                    if (!marker.custom.secondary) {
                      if (
                        Array.isArray(callMarkers) &&
                        callMarkers.length &&
                        (!callMarkers[0].custom.secondary ||
                          (callMarkers[0].custom.secondary && idx !== 0))
                      ) {
                        // Calculate opacity for this marker
                        const shouldDim = shouldMarkerDim(
                          marker.custom.callerID,
                          {
                            activeQueueTab,
                            hasActiveCallFilters,
                            callQueue,
                            callFilters,
                            workStation,
                          },
                        );
                        const isOpaque = !shouldDim;
                        let markerIcon = getCallMarkerIcon({
                          call,
                          isAllView,
                          isMostRecent: idx === mostRecentMarkerIdx,
                          isSecondary: false,
                          hasCallStatusAccess,
                          isOpaque,
                        });
                        if (
                          marker?.instance?.uid ===
                          response?.results[0]?.graphic?.uid
                        ) {
                          let circleColor = getCallCircleColor();
                          markerIcon = getCallMarkerHoverIcon({
                            call,
                            isAllView,
                            isMostRecent: idx === mostRecentMarkerIdx,
                            hasCallStatusAccess,
                            isOpaque,
                          });

                          const symbolOptions = {
                            ...baseEsriCircle,
                            color: circleColor,
                            outline: {
                              ...baseEsriCircle.outline,
                              color: circleColor,
                            },
                          };

                          callMarkerCircles[idx].changeCircleColor({
                            symbolOptions,
                          });
                        }

                        const markerSize = getCallMarkerSize(
                          isAllView,
                          idx === mostRecentMarkerIdx,
                          call,
                        );
                        const options = {
                          icon: markerIcon,
                          iconSize: markerSize,
                        };
                        marker.changeMarkerIcon(options);
                      }
                    }

                    if (
                      marker?.instance?.uid ===
                      response?.results[0]?.graphic?.uid
                    ) {
                      setMarkerHoveredData({
                        callerID: marker.custom.callerID,
                        isSecondaryLocation: marker.custom.secondary,
                      });

                      let data = {
                        callData: call?.core?.locations[idx],
                        callStatus: mostRecentCallStatus,
                      };

                      if (callMarkers[0].custom.secondary && idx > 0) {
                        data.callData = call?.core?.locations[idx - 1];
                      }
                      if (marker.custom.secondary) {
                        data.callData =
                          call?.core?.secondaryLocations[
                            call?.core?.secondaryLocations?.length - 1
                          ];
                      }
                      handlePopup(data);
                    }
                  });
                } else {
                  // Note: looping through all markers may not be the optimal solution. Look into using a prevHoveredMarker value. TBD if possible due to event listener
                  callMarkers.forEach((marker, idx) => {
                    const call = callQueue[marker.custom.callerID];
                    if (!marker.custom.secondary) {
                      if (
                        !callMarkers[0].custom.secondary ||
                        (callMarkers[0].custom.secondary && idx !== 0)
                      ) {
                        let markerIcon = getCallMarkerIcon({
                          call: call,
                          isAllView,
                          isMostRecent: idx === mostRecentMarkerIdx,
                          isSecondary: false,
                          hasCallStatusAccess,
                        });

                        const markerSize = getCallMarkerSize(
                          isAllView,
                          idx === mostRecentMarkerIdx,
                          call,
                        );
                        const options = {
                          icon: markerIcon,
                          iconSize: markerSize,
                        };
                        marker.changeMarkerIcon(options);
                        const circleColor = getCallCircleColor(
                          idx < mostRecentMarkerIdx,
                        );

                        const symbolOptions = {
                          ...baseEsriCircle,
                          color: circleColor,
                          outline: {
                            ...baseEsriCircle.outline,
                            color: circleColor,
                          },
                        };
                        callMarkerCircles[idx].changeCircleColor({
                          symbolOptions,
                        });
                      }
                    }
                  });
                  setMarkerHoveredData({
                    callerID: null,
                    isSecondaryLocation: false,
                  });
                }
              });
            }
          });
          callMarkersLayerHoverHandlerRef.current = hoverHandler;
        }
        break;
      default:
        return null;
    }
  };

  /**
   * Function to handle showing/hiding a popup for a given call marker
   * @param {Object} data - the popup's data used for rendering CallPopup
   */
  function handlePopup(data) {
    // TODO: the header of the popup isn't hideable by default
    if (!data) {
      prevPopupData = null;
      setPopupData(null);
      map.mapInstance.view.popup.location = null;
      map.mapInstance.view.popup.content = null;
      map.mapInstance.view.popup.visible = false;
    } else if (!isEqual(data, prevPopupData)) {
      setPopupData(data);
      prevPopupData = data;
      map.mapInstance.view.popup.content = portalMount;
      map.mapInstance.view.popup.location = {
        y: data.callData.latitude,
        x: data.callData.longitude,
      };
      map.mapInstance.view.popup.visible = true;
      map.mapInstance.view.popup.alignment = 'top-center';
    }
  }

  // Handle map related things on first mount
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        if (
          map &&
          Object.keys(map?.mapInstance.map).length &&
          Object.keys(map?.mapInstance.view).length
        ) {
          if (!polylineLayer) {
            createGraphicsLayer({
              configs: { id: CALL_MARKERS_POLYLINE_LAYER },
            }).then(layer => {
              // Add graphics layer at the end of layers collection to ensure markers are displayed
              // on top of other layer types.
              // https://developers.arcgis.com/javascript/latest/api-reference/esri-Map.html#add
              map.mapInstance.map.layers.push(layer);
              setPolylineLayer(layer);
            });
          }
          if (!callMarkerCirclesLayer) {
            createGraphicsLayer({
              configs: { id: CALL_MARKER_CIRCLES_LAYER },
            }).then(layer => {
              map.mapInstance.map.layers.push(layer);
              setCallMarkerCirclesLayer(layer);
            });
          }
          if (!callMarkersLayer) {
            createGraphicsLayer({
              configs: { id: CALL_MARKERS_LAYER },
            }).then(layer => {
              map.mapInstance.map.layers.push(layer);
              setCallMarkersLayer(layer);
            });
          }
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Add handling for marker clicks/hover with Esri maps
  useEffect(() => {
    // Better to handle the click with esri after all markers are drawn. There is no individual click handling on markers. It's done through the view
    switch (map.mapProvider) {
      case ESRI:
        if (
          callMarkersLayer?.graphics &&
          Array.isArray(callMarkers) &&
          callMarkers.length
        ) {
          handleMarkerClick();

          (async () => {
            if (callMarkersLayerHoverHandlerRef.current) {
              await callMarkersLayerHoverHandlerRef.current.remove();

              callMarkersLayerHoverHandlerRef.current = null;
            }

            handleMarkerHover();
          })();
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callMarkers, callMarkerCircles, isMapIconDisabled]);

  // Refocus on w3w removal
  useEffect(() => {
    if (!hasW3W && selectedCallerID && callMarkers.length > 0) {
      panMapTo({
        mapProvider: ESRI,
        mapInstance: map.mapInstance,
        latitude: callMarkers[0].instance.point.y,
        longitude: callMarkers[0].instance.point.x,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasW3W]);

  // Clear all markers and redraw
  useEffect(() => {
    // All relevant Layers should be created by now
    switch (map.mapProvider) {
      case ESRI: {
        if (
          callMarkersLayer &&
          callMarkerCirclesLayer &&
          (isAllView || selectedCallerID)
        ) {
          clearAll().then(() => {
            if (!selectedAlertID) {
              drawMarkers(userPannedAway);
            }
          });
        }
        break;
      }
      default:
        return null;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    isAllView,
    selectedCallerID,
    callMarkersLayer,
    callMarkerCirclesLayer,
    userPannedAway,
  ]);

  // Redraw markers when call taker mode filters change
  useEffect(() => {
    // Only redraw if layers are ready and we have markers to potentially redraw
    const shouldRedraw = callMarkersLayer && (isAllView || selectedCallerID);

    if (shouldRedraw && map.mapProvider === ESRI) {
      clearAll().then(() => {
        drawMarkers(userPannedAway);
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    callFilters,
    hasActiveCallFilters,
    workStation,
    activeQueueTab,
    userPannedAway,
  ]);

  // Handle changing marker icon
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        if (isAllView) {
          //  Update icon for hovered call and revert others
          callMarkers.forEach((marker, idx) => {
            const call = callQueue[marker.custom.callerID];
            const mostRecentMarkerIdx = callMarkers.length - 1;
            if (call) {
              // Determine if marker should be dimmed based on call taker mode filters
              const shouldDim = shouldMarkerDim(marker.custom.callerID, {
                activeQueueTab,
                hasActiveCallFilters,
                callQueue,
                callFilters,
                workStation,
              });
              const isOpaque = !shouldDim;
              const markerIcon =
                marker?.custom?.callerID === hoveredCall
                  ? getCallMarkerHoverIcon({
                      call,
                      isAllView,
                      isMostRecent: idx === mostRecentMarkerIdx,
                      hasCallStatusAccess,
                      isOpaque,
                    })
                  : getCallMarkerIcon({
                      call: call,
                      isAllView,
                      isMostRecent: idx === mostRecentMarkerIdx,
                      isSecondary: false,
                      hasCallStatusAccess,
                      isOpaque,
                    });

              const markerSize = getCallMarkerSize(
                isAllView,
                idx === mostRecentMarkerIdx,
                call,
              );
              const options = {
                icon: markerIcon,
                iconSize: markerSize,
              };
              marker.changeMarkerIcon(options);
              const mostRecentCallsLocation =
                call?.core?.locations[call?.core?.locations?.length - 1] ||
                call?.core?.secondaryLocations[
                  call?.core?.secondaryLocations?.length - 1
                ];

              // Popup handling
              if (
                marker.custom.callerID === hoveredCall &&
                mostRecentCallsLocation?.longitude &&
                mostRecentCallsLocation?.latitude
              ) {
                const data = {
                  callData: mostRecentCallsLocation,
                  callStatus: mostRecentCallStatus,
                };
                handlePopup(data);
              }
            }
          });

          if (!hoveredCall) {
            handlePopup();
          }
        } else {
          if (hoveredCall && hoveredCall === selectedCallerID) {
            // This prevents handlePopup from triggering twice (here and handleMarkerHover) when hovering over the marker on the map
            if (hoveredFrom === 'queue') {
              const call = callQueue[hoveredCall];
              const mostRecentCallsLocation =
                call?.core?.locations[call?.core?.locations?.length - 1] ||
                call?.core?.secondaryLocations[
                  call?.core?.secondaryLocations?.length - 1
                ];

              const data = {
                callData: mostRecentCallsLocation,
                callStatus: mostRecentCallStatus,
              };

              if (
                mostRecentCallsLocation?.longitude &&
                mostRecentCallsLocation?.latitude
              ) {
                handlePopup(data);
              }
            }
          } else {
            handlePopup();
          }
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoveredCall, mostRecentCallStatus]);

  // Handle incoming locations
  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI: {
        /**
         * Handling race condition for iRP users
         * this checks if `numberWithNewLocation` came from iRP/deeplink
         * prevents a crash where detailed view has no markers
         */
        const shouldCheckQueryUI = queryURI && callMarkers.length === 0;
        const parsedQueryURI =
          queryURI && queryURI.includes('tel') && shouldCheckQueryUI
            ? queryURI.split('tel://')[1]
            : null;

        if (
          numberWithNewLocation &&
          map.mapInstance &&
          numberWithNewLocation !== parsedQueryURI
        ) {
          const incomingMarkerType = getIncomingMarkerType(
            numberWithNewLocation,
          );
          if (isAllView) {
            const call = callQueue[numberWithNewLocation];
            const mostRecentCallsLocation =
              call?.core?.locations[call?.core?.locations?.length - 1] ||
              call?.core?.secondaryLocations[
                call?.core?.secondaryLocations?.length - 1
              ];
            const latlng = {
              lat: parseFloat(mostRecentCallsLocation?.latitude),
              lng: parseFloat(mostRecentCallsLocation?.longitude),
            };
            const isAniAli = isSourceAniAli(mostRecentCallsLocation);
            const isSecondary =
              isAniAli &&
              mostRecentCallsLocation?.class_of_service_category?.startsWith(
                PHASE,
              );
            // If the call has an existing location/marker, update it
            const currentMarker = callMarkers.find(marker => {
              return marker.custom.callerID === numberWithNewLocation;
            });
            const markerSize = getCallMarkerSize(
              isAllView,
              false,
              callQueue[numberWithNewLocation],
            );
            if (currentMarker) {
              const options = {
                position: latlng,
              };
              currentMarker.updatePosition(options);
              // Determine if marker should be dimmed based on call taker mode filters
              const shouldDim = shouldMarkerDim(numberWithNewLocation, {
                activeQueueTab,
                hasActiveCallFilters,
                callQueue,
                callFilters,
                workStation,
              });
              const isOpaque = !shouldDim;
              const markerIcon = getCallMarkerIcon({
                call: callQueue[numberWithNewLocation],
                isAllView,
                isMostRecent: true,
                isSecondary,
                hasCallStatusAccess,
                isOpaque,
              });

              currentMarker.changeMarkerIcon({
                icon: markerIcon,
                iconSize: markerSize,
              });
              onMarkerAdd();
            } else {
              // draw marker, add to layer, set state
              // Determine if marker should be dimmed based on call taker mode filters
              const shouldDim = shouldMarkerDim(numberWithNewLocation, {
                activeQueueTab,
                hasActiveCallFilters,
                callQueue,
                callFilters,
                workStation,
              });
              const isOpaque = !shouldDim;
              const markerIcon = getCallMarkerIcon({
                call: callQueue[numberWithNewLocation],
                isAllView,
                isMostRecent: true,
                isSecondary,
                hasCallStatusAccess,
                isOpaque,
              });
              createMarker({
                mapObject: map,
                options: {
                  point: latlng,
                  icon: markerIcon,
                  iconSize: markerSize,
                  custom: {
                    callerID: numberWithNewLocation,
                    secondary: isSecondary,
                  },
                  layer: callMarkersLayer,
                },
                markerType: MARKER_TYPE_GRAPHIC,
              }).then(resolvedMarker => {
                callMarkersLayer.add(resolvedMarker.instance);
                onMarkerAdd();
                const normalizedCallMarkers = [
                  resolvedMarker,
                  ...callMarkers,
                ].filter(m => callQueue[m.custom.callerID]);
                setCallMarkers(normalizedCallMarkers);
                return resolvedMarker;
              });
            }
          } else if (
            numberWithNewLocation === selectedCallerID &&
            callMarkers.length
          ) {
            const currCalls = callQueue[selectedCallerID]?.core;

            const call = callQueue[numberWithNewLocation];
            const callLocations = call?.core?.locations;
            const callLocation =
              call?.core?.locations[call?.core?.locations?.length - 1];
            const secondaryLocations = call?.core?.secondaryLocations;
            const secondaryLocation =
              call?.core?.secondaryLocations[
                call?.core?.secondaryLocations?.length - 1
              ];

            const prevCallLocations =
              previousCallQueue[numberWithNewLocation]?.core?.locations;
            const prevSecondaryLocations =
              previousCallQueue[numberWithNewLocation]?.core
                ?.secondaryLocations;

            if (
              callLocations &&
              prevCallLocations &&
              callLocations?.length === prevCallLocations?.length &&
              secondaryLocations?.length === prevSecondaryLocations?.length
            ) {
              onMarkerAdd();
              return;
            }
            if (
              currCalls?.locations?.length &&
              currCalls.secondaryLocations?.length
            ) {
              clearAllPolylines().then(() =>
                drawLine([
                  currCalls?.locations[currCalls?.locations?.length - 1],
                  currCalls.secondaryLocations[
                    currCalls.secondaryLocations?.length - 1
                  ],
                ]),
              );
            }

            let currentMarker;
            for (let i = callMarkers.length - 1; i >= 0; i--) {
              let marker = callMarkers[i];
              if (
                incomingMarkerType === LOCATION &&
                marker.custom.callerID === numberWithNewLocation &&
                !marker.custom.secondary
              ) {
                currentMarker = marker;
                break;
              } else if (
                incomingMarkerType === SECONDARY_LOCATION &&
                marker.custom.callerID === numberWithNewLocation &&
                marker.custom.secondary
              ) {
                currentMarker = marker;
                break;
              }
            }
            const latlng = {
              lat: parseFloat(callLocation?.latitude),
              lng: parseFloat(callLocation?.longitude),
            };

            if (currentMarker && incomingMarkerType === LOCATION) {
              // Create new marker
              // Determine if marker should be dimmed based on call taker mode filters
              const shouldDim = shouldMarkerDim(numberWithNewLocation, {
                activeQueueTab,
                hasActiveCallFilters,
                callQueue,
                callFilters,
                workStation,
              });
              const isOpaque = !shouldDim;
              const markerIcon = getCallMarkerIcon({
                call,
                isAllView,
                isMostRecent: true,
                isSecondary: false,
                hasCallStatusAccess,
                isOpaque,
              });
              const markerSize = getCallMarkerSize(isAllView, true, call);
              createMarker({
                mapObject: map,
                options: {
                  point: latlng,
                  icon: markerIcon,
                  iconSize: markerSize,
                  custom: {
                    callerID: numberWithNewLocation,
                    secondary: incomingMarkerType === SECONDARY_LOCATION,
                  },
                  layer: callMarkersLayer,
                },
                markerType: MARKER_TYPE_GRAPHIC,
              }).then(resolvedMarker => {
                // Update current marker
                const newIcon = getCallMarkerIcon({
                  call,
                  isAllView,
                  isMostRecent: false,
                  isSecondary: false,
                  hasCallStatusAccess,
                  isOpaque,
                });
                const markerSize = getCallMarkerSize(isAllView, false, call);
                currentMarker.changeMarkerIcon({
                  icon: newIcon,
                  iconSize: markerSize,
                });
                if (!isSoupMapImprovementsOn || !userPannedAway) {
                  panMapTo({
                    mapProvider: ESRI,
                    mapInstance: map.mapInstance,
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                  });
                }
                onMarkerAdd();
                callMarkersLayer.add(resolvedMarker.instance);

                setCallMarkers([...callMarkers, resolvedMarker]);
              });

              let currentCircle;
              for (let i = callMarkerCircles.length - 1; i >= 0; i--) {
                let circle = callMarkerCircles[i];
                if (circle.custom.callerID === numberWithNewLocation) {
                  currentCircle = circle;
                  break;
                }
              }
              if (currentCircle) {
                // Old circle
                const circleColorTransparent = getCallCircleColor(true);

                const symbolOptions = {
                  ...baseEsriCircle,
                  color: circleColorTransparent,
                  outline: {
                    ...baseEsriCircle.outline,
                    color: circleColorTransparent,
                  },
                  visible: false,
                };
                currentCircle.changeCircleColor({ symbolOptions });
                // New circle
                const circleOptions = {
                  center: [latlng.lng, latlng.lat],
                  radius:
                    callLocation.uncertainty_radius < 0
                      ? 0
                      : parseFloat(callLocation.uncertainty_radius),
                };
                createCircle({
                  mapObject: map,
                  circleOptions: {
                    ...circleOptions,
                    custom: {
                      callerID: numberWithNewLocation,
                      secondary: incomingMarkerType === SECONDARY_LOCATION,
                    },
                    layer: callMarkerCirclesLayer,
                  },
                  graphicOptions: {
                    symbol: baseEsriCircle,
                    visible: true,
                  },
                }).then(resolvedMarkerCircle => {
                  callMarkerCirclesLayer.add(resolvedMarkerCircle.instance);
                  setCallMarkerCircles([
                    ...callMarkerCircles,
                    resolvedMarkerCircle,
                  ]);
                });
              }
            } else if (
              currentMarker &&
              incomingMarkerType === SECONDARY_LOCATION
            ) {
              const latlng = {
                lat: parseFloat(secondaryLocation.latitude),
                lng: parseFloat(secondaryLocation.longitude),
              };

              currentMarker.remove();
              // Determine if marker should be dimmed based on call taker mode filters
              const shouldDim = shouldMarkerDim(numberWithNewLocation, {
                activeQueueTab,
                hasActiveCallFilters,
                callQueue,
                callFilters,
                workStation,
              });
              const isOpaque = !shouldDim;
              const markerIcon = getCallMarkerIcon({
                call,
                isAllView,
                isMostRecent: true,
                isSecondary: true,
                hasCallStatusAccess,
                isOpaque,
              });
              const markerSize = getCallMarkerSize(isAllView, true, call);

              createMarker({
                mapObject: map,
                options: {
                  point: latlng,
                  icon: markerIcon,
                  iconSize: markerSize,
                  custom: {
                    callerID: numberWithNewLocation,
                    secondary: incomingMarkerType === SECONDARY_LOCATION,
                  },
                  layer: callMarkersLayer,
                },
                markerType: MARKER_TYPE_GRAPHIC,
              }).then(resolvedMarker => {
                panMapTo({
                  mapProvider: ESRI,
                  mapInstance: map.mapInstance,
                  latitude: latlng.lat,
                  longitude: latlng.lng,
                });
                onMarkerAdd();
                callMarkersLayer.graphics.unshift(resolvedMarker.instance);
                // Append marker to front of array for secondary locations
                setCallMarkers([resolvedMarker, ...callMarkers.slice(1)]);
              });
            } else if (!currentMarker) {
              const mostRecentCallsLocation =
                incomingMarkerType === LOCATION
                  ? callLocation
                  : secondaryLocation;
              const latlng = {
                lat: parseFloat(mostRecentCallsLocation?.latitude),
                lng: parseFloat(mostRecentCallsLocation?.longitude),
              };
              // Determine if marker should be dimmed based on call taker mode filters
              const shouldDim = shouldMarkerDim(numberWithNewLocation, {
                activeQueueTab,
                hasActiveCallFilters,
                callQueue,
                callFilters,
                workStation,
              });
              const isOpaque = !shouldDim;
              const markerIcon = getCallMarkerIcon({
                call,
                isAllView,
                isMostRecent: true,
                isSecondary: incomingMarkerType === SECONDARY_LOCATION,
                hasCallStatusAccess,
                isOpaque,
              });
              const markerSize = getCallMarkerSize(isAllView, true, call);

              if (incomingMarkerType === LOCATION) {
                const currentCircle = callMarkerCircles.find(
                  circle => circle.custom.callerID === numberWithNewLocation,
                );
                if (currentCircle) {
                  // Old circle
                  const circleColorTransparent = getCallCircleColor(true);

                  const symbolOptions = {
                    ...baseEsriCircle,
                    color: circleColorTransparent,
                    outline: {
                      ...baseEsriCircle.outline,
                      color: circleColorTransparent,
                    },
                  };
                  currentCircle.changeCircleColor({ symbolOptions });
                  // New circle
                  const circleOptions = {
                    center: [latlng.lng, latlng.lat],
                    radius:
                      callLocation.uncertainty_radius < 0
                        ? 0
                        : parseFloat(callLocation.uncertainty_radius),
                  };
                  createCircle({
                    mapObject: map,
                    circleOptions: {
                      ...circleOptions,
                      custom: {
                        callerID: numberWithNewLocation,
                        secondary: incomingMarkerType === SECONDARY_LOCATION,
                      },
                      layer: callMarkerCirclesLayer,
                    },
                    graphicOptions: {
                      symbol: baseEsriCircle,
                      visible: true,
                    },
                  }).then(resolvedMarkerCircle => {
                    callMarkerCirclesLayer.add(resolvedMarkerCircle.instance);
                    setCallMarkerCircles([
                      ...callMarkerCircles,
                      resolvedMarkerCircle,
                    ]);
                  });
                }
              } else if (incomingMarkerType === SECONDARY_LOCATION) {
                if (
                  callMarkerCircles.length &&
                  !callMarkerCircles[0].custom.secondary
                ) {
                  const circleColorTransparent = getCallCircleColor(true);
                  const circleOptions = {
                    center: [latlng.lng, latlng.lat],
                    radius:
                      location.uncertainty_radius < 0
                        ? 0
                        : parseFloat(location.uncertainty_radius),
                  };
                  const circle = createCircle({
                    mapObject: map,
                    circleOptions: {
                      ...circleOptions,
                      custom: {
                        callerID: numberWithNewLocation,
                        secondary: true,
                      },
                      layer: callMarkerCirclesLayer,
                    },
                    graphicOptions: {
                      symbol: {
                        ...baseEsriCircle,
                        color: circleColorTransparent,
                        outline: {
                          ...baseEsriCircle.outline,
                          color: circleColorTransparent,
                        },
                      },
                      visible: true,
                    },
                  }).then(resolvedMarkerCircle => {
                    callMarkerCirclesLayer.add(resolvedMarkerCircle.instance);
                    return resolvedMarkerCircle;
                  });

                  setCallMarkerCircles([circle, ...callMarkerCircles]);
                }
              }

              createMarker({
                mapObject: map,
                options: {
                  point: latlng,
                  icon: markerIcon,
                  iconSize: markerSize,
                  custom: {
                    callerID: numberWithNewLocation,
                    secondary: incomingMarkerType === SECONDARY_LOCATION,
                  },
                  layer: callMarkersLayer,
                },
                markerType: MARKER_TYPE_GRAPHIC,
              }).then(resolvedMarker => {
                if (!isSoupMapImprovementsOn || !userPannedAway) {
                  panMapTo({
                    mapProvider: ESRI,
                    mapInstance: map.mapInstance,
                    latitude: latlng.lat,
                    longitude: latlng.lng,
                  });
                }
                onMarkerAdd();
                if (incomingMarkerType === SECONDARY_LOCATION) {
                  callMarkersLayer.graphics.unshift(resolvedMarker.instance);
                  // Append marker to front of array for secondary locations
                  setCallMarkers([resolvedMarker, ...callMarkers]);
                } else {
                  callMarkersLayer.add(resolvedMarker.instance);
                  setCallMarkers([...callMarkers, resolvedMarker]);
                }
              });
            } else {
              // shouldn't be possible if the call is selected
            }
          } else {
            // Currently viewing another call?. Reset numberWithNewLocation for
            // correct handling
            onMarkerAdd();
          }
        }
        break;
      }
      default:
        return null;
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberWithNewLocation]);

  useEffect(() => {
    // All calls have been removed
    if (map && !Object.keys(callQueue).length) {
      clearAll();
      // Click and hover handlers will be automatically removed for esri maps
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [callQueue]);

  useEffect(() => {
    // Clear all markers, remove click/hover handlers
    if (map && selectedAlertID) {
      clearAll();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedAlertID]);

  useEffect(() => {
    switch (map.mapProvider) {
      case ESRI:
        // Find and remove corresponding marker
        if (callMarkersLayer && callMarkers.length && removedID) {
          const foundMarker = callMarkers.find(marker => {
            return marker.custom.callerID === removedID;
          });
          if (foundMarker) {
            foundMarker.remove();
            const newMarkers = callMarkers.filter(
              marker => marker.custom.callerID !== removedID,
            );
            setCallMarkers(newMarkers);
            onRemoveID(null);
          }
        }
        break;
      default:
        return null;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [removedID]);

  useEffect(() => {
    return () => {
      prevPopupData = null;
    };
  }, []);

  useEffect(() => {
    if (callMarkersLayer && activeQueueTab === EMERGENCY_CALLS) {
      callMarkersLayer.opacity = 1;
    } else if (callMarkersLayer && activeQueueTab === ALERTS) {
      callMarkersLayer.opacity = 0.5;
    }
  }, [activeQueueTab, callMarkersLayer]);

  // When a marker is hovered, set the callerID and isSecondaryLocation to a state hook which
  // callback the onMarkerHover function from JurisdictionView.js.
  useEffect(() => {
    if (markerHoveredData.callerID) {
      onMarkerHover(
        markerHoveredData.callerID,
        markerHoveredData.isSecondaryLocation,
      );
    } else {
      onMarkerHover(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [markerHoveredData.callerID]);

  // Clean up callMarkersLayerClickHandler
  useEffect(() => {
    return () => {
      if (callMarkersLayerClickHandler) {
        callMarkersLayerClickHandler.remove();
      }
    };
  }, [callMarkersLayerClickHandler]);

  // Conditional rendering based on map provider
  switch (map.mapProvider) {
    case ESRI:
      if (popupData) {
        return (
          <PopupPortal mount={portalMount}>
            <CallPopup
              isAllView={isAllView}
              popupData={popupData}
              isInternational={isInternational}
            />
          </PopupPortal>
        );
      }
      return null;
    default:
      return null;
  }
};
